import { useCallback, useContext, useState } from "react";
import { notBlank } from "../util/ext";
import {
  useCurationCreate,
  useSubscriptionDelete,
} from "./subscriptionService";
import { AlertContext } from "../components/alert/AlertProvider";

const useSubscriptionHandler = (
  { initialDialogState = false, callbacks = {} } = {
    initialDialogState: false,
    callbacks: {},
  },
) => {
  const { alertError } = useContext(AlertContext);
  const [dialogOpen, setDialogOpen] = useState(initialDialogState);
  const [loading, setLoading] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const handleSubscriptionEditClick = useCallback(subscription => {
    setCurrentSubscription(subscription);
    setDialogOpen(true);
  }, []);

  const handleNewSubscriptionClicked = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleSubscriptionDialogClose = useCallback(() => {
    setDialogOpen(false);
    setCurrentSubscription(null);
  }, []);

  const handleSubscriptionDialogSave = useCallback(
    async subs => {
      setDialogOpen(false);
      setCurrentSubscription(null);
      if (notBlank(callbacks.afterSave)) {
        await callbacks.afterSave(subs);
      }
    },
    [callbacks],
  );

  const subscriptionDelete = useSubscriptionDelete();
  const handleSubscriptionDeleteClick = useCallback(
    async subscription => {
      setLoading([subscription.id]);
      try {
        await subscriptionDelete.mutateAsync({
          resourceIds: { id: subscription.id },
        });
        if (notBlank(callbacks.afterDelete)) {
          await callbacks.afterDelete(subscription);
        }
      } catch (err) {
        alertError(err);
      } finally {
        setLoading([]);
      }
    },
    [subscriptionDelete, callbacks, alertError],
  );

  const curationCreate = useCurationCreate();
  const handleCreateCurationClick = useCallback(
    async subscription => {
      try {
        await curationCreate.mutateAsync({
          data: {
            organizationId: subscription.organizationId,
            moduleId: subscription.mediaId,
          },
        });
        if (notBlank(callbacks.afterCurationCreate)) {
          await callbacks.afterCurationCreate(subscription);
        }
      } catch (err) {
        if (notBlank(callbacks.onCurationCreateError)) {
          callbacks.onCurationCreateError(err);
        }
      }
    },
    [curationCreate, callbacks],
  );

  return {
    dialogOpen,
    currentSubscription,
    handleSubscriptionEditClick,
    handleNewSubscriptionClicked,
    handleSubscriptionDialogClose,
    handleSubscriptionDialogSave,
    handleSubscriptionDeleteClick,
    handleCreateCurationClick,
    loading,
  };
};

export { useSubscriptionHandler };
